import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { computed, observable } from 'mobx-angular';
import { TranslateService } from '@ngx-translate/core';
import { sub } from 'date-fns';
import { firstValueFrom, from, Subscription } from 'rxjs';
import { UserService } from 'netsocial-posting/src/app/services/user/user.service';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import {
  ApiRequestService,
  NetSocialAccountFields,
} from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { buildHash } from 'netsocial-lib/src/app/utils/array-utils';
import { formatForApi } from 'netsocial-lib/src/app/utils/date-utils';
import { propertyDidChange } from 'netsocial-lib/src/app/utils/component-utils';
import {
  GetTimeSeriesParams,
  ReportingService,
  TimeseriesRecord,
} from '../../../services/reporting/reporting.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';

@Component({
  selector: 'performance-chart',
  templateUrl: './performance-chart.component.html',
  styleUrls: ['./performance-chart.component.scss'],
})
export class PerformanceChartComponent implements OnInit, OnChanges, OnDestroy {
  private labelCounter = 0;

  @Input() showLink = true;

  @observable @Input() endDate = new Date();
  @observable @Input() startDate = sub(this.endDate, { days: 7 });

  @Input() accountService: GetTimeSeriesParams['accountService'];
  @Input() showSpanSelector: boolean;
  @Input() hidePeriod: boolean;
  @Input() hideView: boolean;

  public selectedMetric: GetTimeSeriesParams['type'] = 'earnedValue';

  @observable public data?: TimeseriesRecord[] = [];
  @observable public comparisonData?: TimeseriesRecord[] = [];

  @observable public options: Array<GetTimeSeriesParams['type']> = [
    'clicks',
    'earnedValue',
    'impressions',
    'likes',
    'shares',
    'deliveries',
  ];

  @observable public periodOptions = [7, 30, 90];

  public selectedPeriod = this.periodOptions[0];

  @observable public viewOptions: Array<GetTimeSeriesParams['view']> = [
    'personal',
    'company',
    'organization',
  ];

  @Input() public selectedView: GetTimeSeriesParams['view'] = 'personal';

  @observable @Input() endDateLabel = this.endDate.toDateString();
  @observable @Input() startDateLabel = this.startDate.toDateString();

  colorScheme = {
    domain: ['#f7941d', '#391d81'],
  };

  private contextOwnerSubscription: Subscription;

  @computed
  public get isAdmin() {
    return this.userService.currentUser?.isAdmin;
  }

  @computed
  public get lineChartData() {
    const parseData = (record: TimeseriesRecord) => {
      if (
        record.current &&
        record.current.startDate &&
        record.current.endDate &&
        record.current.value
      ) {
        const startDateObject = new Date(record.current.startDate);
        const endDateObject = new Date(record.current.endDate);
        const startDateSplit = startDateObject.toDateString().split(' ').splice(0, 3).join(' ');
        const endDateSplit = endDateObject.toDateString().split(' ').splice(0, 3).join(' ');
        return {
          change: parseInt(record.change).toFixed(0) + '%',
          name: endDateSplit,
          indication: record.indication,
          startDate: startDateSplit,
          endDate: endDateSplit,
          value: Number(record.current.value).toFixed(2),
          currentData: 1,
        };
      }
    };

    const parsePastData = (record: TimeseriesRecord) => {
      if (
        record.past &&
        record.past.startDate &&
        record.past.endDate &&
        record.past.value &&
        record.current &&
        record.current.startDate
      ) {
        // const currStartDateObject = new Date(record.current.startDate);
        const currEndDateObject = new Date(record.current.endDate);
        const pastStartDateObject = new Date(record.past.startDate);
        const pastEndDateObject = new Date(record.past.endDate);
        // const currStartDateSplit = currStartDateObject.toDateString().split(' ').splice(0,3).join(' ');
        const currEndDateSplit = currEndDateObject
          ?.toDateString()
          .split(' ')
          .splice(0, 3)
          .join(' ');
        const pastStartDateSplit = pastStartDateObject
          .toDateString()
          .split(' ')
          .splice(0, 3)
          .join(' ');
        const pastEndDateSplit = pastEndDateObject
          ?.toDateString()
          .split(' ')
          .splice(0, 3)
          .join(' ');
        return {
          name: currEndDateSplit,
          startDate: pastStartDateSplit,
          endDate: pastEndDateSplit,
          value: Number(record.past.value),
          currentData: 0,
        };
      }
    };

    const data: Array<{
      name: string;
      series: unknown[];
    }> = [];

    data.push({
      name: this.currentText,
      series: this.data ? this.data?.map(parsePastData).filter((data) => !!data) : [],
    });

    data.push({
      name: this.pastText,
      series: this.data ? this.data?.map(parseData).filter((data) => !!data) : [],
    });

    return data;
  }

  @computed
  public get chartIsEmpty() {
    return this.lineChartData.reduce((chartIsEmpty, series) => {
      if (series.series?.length !== 0) {
        chartIsEmpty = false;
      }
      return chartIsEmpty;
    }, true);
  }

  @observable public pastText = 'pastText';
  @observable public currentText = 'currentText';

  @observable public getDataSubscription?: Subscription;
  @observable public getComparisonDataSubscription?: Subscription;
  @observable private contextOwner?: NetSocialAccountFields;

  @computed
  public get isLoading() {
    return !!(this.getDataSubscription || this.getComparisonDataSubscription);
  }

  @computed
  public get isDesktop() {
    return this.responsiveService.isDesktop;
  }

  constructor(
    private apiRequest: ApiRequestService,
    private userService: UserService,
    private translate: TranslateService,
    private errorService: ErrorService,
    private reportingService: ReportingService,
    private responsiveService: ResponsiveService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.contextOwnerSubscription = this.apiRequest.getContextOwner().subscribe((value) => {
      this.contextOwner = value;
      this.getData();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (propertyDidChange(changes).length) {
      this.getData();
    }
    if (this.startDate) {
      this.startDateLabel = this.startDate.toDateString();
    }
  }

  ngOnDestroy() {
    this.contextOwnerSubscription.unsubscribe();
    this.getDataSubscription?.unsubscribe();
  }

  xAxis = (label: string) => {
    // label = this.labelCounter % 3 === 0 ? label : '';
    // this.labelCounter++;
    return label;
  };

  public setDataByPeriod() {
    const periodNumber = this.selectedPeriod;
    this.endDate = new Date();
    this.startDate = sub(this.endDate, { days: periodNumber });
    this.getData();
  }

  private async loadTranslations() {
    this.pastText = await firstValueFrom(this.translate.get(this.pastText));
    this.currentText = await firstValueFrom(this.translate.get(this.currentText));
  }

  getData() {
    let selectedClientId: string | number | undefined = '';
    if (this.contextOwner?.agency === '1' && this.userService.netSocialAccountsInput) {
      const client = this.selectedView;
      const clientHash = buildHash(this.userService.netSocialAccountsInput.items, 'name');
      selectedClientId = clientHash[client]?.ownerId;
      this.selectedView = selectedClientId ? 'organization' : 'company';
    }

    this.labelCounter = 0;

    const params: GetTimeSeriesParams = {
      type: this.selectedMetric,
      startDate: formatForApi(this.startDate),
      endDate: formatForApi(this.endDate),
      accountService: this.accountService,
      view: this.selectedView,
    };

    if (this.contextOwner?.agency === '1') {
      params.clientFilter = selectedClientId;
    }

    if (params.accountService) {
      params.accountService = params.accountService.filter(
        (service) => !service.includes('instagram')
      );
    }

    this.getDataSubscription = from(
      this.errorService.resolve(this.reportingService.getTimeSeries(params))
    ).subscribe((data) => {
      this.data = data;
      this.getDataSubscription = undefined;
    });
  }
}
